import AddRounded from '@mui/icons-material/AddRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { businessDate, dateTime } from '../../lib/format';
import { errorMessage, useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { WHATSAPP_MODELS } from '../whatsapp/WhatsappMessages';
import {
  useEmailAdmin,
  useEmailStatus,
  type EmailDocumentType,
  type EmailServer,
  type EmailStatus,
  type MailSecurity,
} from './api';
import { EmailTemplates } from './EmailTemplates';

/** The documents Email can send, in the order the shop sees them (the same as WhatsApp). */
export const EMAIL_MODELS: { type: EmailDocumentType; label: string }[] = WHATSAPP_MODELS;

/** Common providers: fills host, port and security (the user still adds their own login). */
const PRESETS: { id: string; label: string; host: string; port: number; security: MailSecurity }[] =
  [
    {
      id: 'gmail',
      label: 'Gmail / Google Workspace',
      host: 'smtp.gmail.com',
      port: 465,
      security: 'SSL',
    },
    {
      id: 'm365',
      label: 'Microsoft 365 / Outlook',
      host: 'smtp.office365.com',
      port: 587,
      security: 'STARTTLS',
    },
    {
      id: 'godaddy',
      label: 'GoDaddy',
      host: 'smtpout.secureserver.net',
      port: 465,
      security: 'SSL',
    },
    { id: 'zoho', label: 'Zoho Mail', host: 'smtp.zoho.com', port: 465, security: 'SSL' },
  ];

/**
 * Settings → Email: the shop's own outgoing mail servers (as many as PackBytes allows), which
 * documents each one emails (a document belongs to one server; the Email button shows only on
 * documents given to a server), a test email per server, and the subject and message per document.
 */
export function EmailTab() {
  const { can } = useAuth();
  const { format } = useShop();
  const status = useEmailStatus();
  const [editing, setEditing] = useState<EmailServer | 'new' | null>(null);
  const [testing, setTesting] = useState<EmailServer | null>(null);
  const [removing, setRemoving] = useState<EmailServer | null>(null);
  const admin = useEmailAdmin();
  const notify = useNotify();
  const canManage = can('email.manage');
  const data = status.data;
  const plugin = data?.plugin;

  if (status.isLoading) return <CircularProgress />;
  if (!data || !plugin || plugin.state === 'NOT_ENABLED')
    return (
      <Alert severity="info">
        Email is an add-on. See the Subscription page to ask PackBytes for it.
      </Alert>
    );

  const servers = data.servers ?? [];
  const limit = plugin.units;
  const owner = (type: EmailDocumentType) => data.documents[type];

  return (
    <Stack spacing={2.5}>
      {plugin.state === 'EXPIRED' && (
        <Alert severity="error">
          Email subscription ended, grace used. Contact PackBytes to renew it.
        </Alert>
      )}
      {plugin.state === 'GRACE' && plugin.graceEndsAt && (
        <Alert severity="warning">
          The Email period has ended; it keeps working until{' '}
          {businessDate(plugin.graceEndsAt.slice(0, 10), format)}. Contact PackBytes to renew it.
        </Alert>
      )}
      {!plugin.shopEnabled && (
        <Alert severity="info">
          Email is hidden in the shop. Switch it on from the Subscription page.
        </Alert>
      )}
      <Card>
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 2 }}>
            <MailOutlineRounded color="primary" sx={{ fontSize: 32 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6">Outgoing mail servers</Typography>
              <Typography variant="body2" color="text.secondary">
                {servers.length} of {limit} used. Give each kind of document to one server; only
                those documents get an Email button.
              </Typography>
            </Box>
            {canManage && (
              <Button
                variant="contained"
                startIcon={<AddRounded />}
                disabled={servers.length >= limit || plugin.state === 'EXPIRED'}
                onClick={() => setEditing('new')}
              >
                Add server
              </Button>
            )}
          </Stack>
          {!canManage ? (
            <Typography variant="body2">Managing mail servers needs “Manage email”.</Typography>
          ) : servers.length === 0 ? (
            <Typography variant="body2">
              No mail server yet. Add the shop’s email account (Gmail, Microsoft 365, GoDaddy or any
              SMTP server) to start emailing documents.
            </Typography>
          ) : (
            <Stack spacing={0}>
              {servers.map((s) => (
                <Box key={s.id} sx={{ borderTop: 1, borderColor: 'divider', py: 2 }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{ alignItems: { sm: 'center' } }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                      >
                        <Typography sx={{ fontWeight: 600 }}>{s.name}</Typography>
                        {!s.usable && (
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label="Over the limit"
                          />
                        )}
                        {s.testError ? (
                          <Chip size="small" color="error" variant="outlined" label="Test failed" />
                        ) : s.testedAt ? (
                          <Chip size="small" color="success" variant="outlined" label="Tested" />
                        ) : (
                          <Chip size="small" variant="outlined" label="Not tested" />
                        )}
                      </Stack>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ wordBreak: 'break-all' }}
                      >
                        {s.fromName ? `${s.fromName} <${s.fromAddress}>` : s.fromAddress} · {s.host}
                        :{s.port}
                        {s.testedAt && !s.testError
                          ? ` · tested ${dateTime(s.testedAt, format)}`
                          : ''}
                      </Typography>
                      {s.testError && (
                        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                          {s.testError}
                        </Typography>
                      )}
                      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {s.documentTypes.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            No documents given to this server yet.
                          </Typography>
                        ) : (
                          EMAIL_MODELS.filter((m) => s.documentTypes.includes(m.type)).map((m) => (
                            <Chip key={m.type} size="small" label={m.label} />
                          ))
                        )}
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      <Button
                        size="small"
                        startIcon={<SendRounded />}
                        onClick={() => setTesting(s)}
                      >
                        Test
                      </Button>
                      <Button
                        size="small"
                        startIcon={<EditRounded />}
                        onClick={() => setEditing(s)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteOutlineRounded />}
                        onClick={() => setRemoving(s)}
                      >
                        Remove
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
          {servers.length >= limit && canManage && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Need more mail servers? Contact PackBytes.
            </Typography>
          )}
        </CardContent>
      </Card>
      <EmailTemplates status={data} canManage={canManage} />
      {editing && (
        <ServerDialog
          server={editing === 'new' ? null : editing}
          owner={owner}
          onClose={() => setEditing(null)}
        />
      )}
      {testing && <TestDialog server={testing} onClose={() => setTesting(null)} />}
      <ConfirmDialog
        open={!!removing}
        title={`Remove ${removing?.name ?? 'server'}?`}
        body="Its documents lose their Email button until you give them to another server. Sent emails stay in the history."
        confirmLabel="Remove"
        danger
        onConfirm={() =>
          admin.remove.mutateAsync(removing?.id ?? '').then(
            () => notify.success('Mail server removed'),
            (e: unknown) => notify.error(e),
          )
        }
        onClose={() => setRemoving(null)}
      />
    </Stack>
  );
}

function ServerDialog({
  server,
  owner,
  onClose,
}: {
  server: EmailServer | null;
  owner: (type: EmailDocumentType) => EmailStatus['documents'][EmailDocumentType];
  onClose: () => void;
}) {
  const admin = useEmailAdmin();
  const notify = useNotify();
  const [name, setName] = useState(server?.name ?? '');
  const [host, setHost] = useState(server?.host ?? '');
  const [port, setPort] = useState(String(server?.port ?? 587));
  const [security, setSecurity] = useState<MailSecurity>(server?.security ?? 'STARTTLS');
  const [username, setUsername] = useState(server?.username ?? '');
  const [password, setPassword] = useState('');
  const [fromName, setFromName] = useState(server?.fromName ?? '');
  const [fromAddress, setFromAddress] = useState(server?.fromAddress ?? '');
  const [replyTo, setReplyTo] = useState(server?.replyTo ?? '');
  const [bccSelf, setBccSelf] = useState(server?.bccSelf ?? false);
  const [allowSelfSigned, setAllowSelfSigned] = useState(server?.allowSelfSigned ?? false);
  const [types, setTypes] = useState<EmailDocumentType[]>(server?.documentTypes ?? []);
  const [error, setError] = useState<string | null>(null);
  const busy = admin.create.isPending || admin.update.isPending;

  const preset = (id: string) => {
    const p = PRESETS.find((x) => x.id === id);
    if (!p) return;
    setHost(p.host);
    setPort(String(p.port));
    setSecurity(p.security);
    if (!name) setName(p.label.split(' /')[0] ?? p.label);
  };

  const submit = () => {
    setError(null);
    const portNo = Number(port);
    if (!name.trim() || !host.trim() || !fromAddress.trim()) {
      setError('Enter a name, the server and the from address.');
      return;
    }
    if (!Number.isInteger(portNo) || portNo < 1 || portNo > 65535) {
      setError('Enter a port between 1 and 65535.');
      return;
    }
    const body = {
      name: name.trim(),
      host: host.trim(),
      port: portNo,
      security,
      username: username.trim() || null,
      fromName: fromName.trim() || null,
      fromAddress: fromAddress.trim(),
      replyTo: replyTo.trim() || null,
      bccSelf,
      allowSelfSigned,
      documentTypes: types,
      ...(password ? { password } : {}),
    };
    const done = {
      onSuccess: () => {
        notify.success(server ? 'Mail server saved' : 'Mail server added');
        onClose();
      },
      onError: (e: unknown) => setError(errorMessage(e)),
    };
    if (server) admin.update.mutate({ id: server.id, version: server.version, ...body }, done);
    else admin.create.mutate(body, done);
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{server ? `Edit ${server.name}` : 'Add a mail server'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {!server && (
            <TextField
              select
              label="Provider"
              defaultValue=""
              onChange={(e) => preset(e.target.value)}
            >
              {PRESETS.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.label}
                </MenuItem>
              ))}
              <MenuItem value="other">Other SMTP server</MenuItem>
            </TextField>
          )}
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Sales"
            required
            slotProps={{ htmlInput: { maxLength: 60 } }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="SMTP server"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="smtp.example.com"
              required
              sx={{ flex: 2 }}
            />
            <TextField
              label="Port"
              value={port}
              onChange={(e) => setPort(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))}
              required
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              sx={{ flex: 1 }}
            />
            <TextField
              select
              label="Security"
              value={security}
              onChange={(e) => setSecurity(e.target.value as MailSecurity)}
              sx={{ flex: 1 }}
            >
              <MenuItem value="SSL">SSL</MenuItem>
              <MenuItem value="STARTTLS">STARTTLS</MenuItem>
              <MenuItem value="NONE">None</MenuItem>
            </TextField>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="User name"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="off"
              sx={{ flex: 1 }}
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              placeholder={server?.hasPassword ? 'Unchanged' : undefined}
              helperText="Gmail and Microsoft 365 need an app password."
              sx={{ flex: 1 }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="From name"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              label="From address"
              value={fromAddress}
              onChange={(e) => setFromAddress(e.target.value)}
              required
              slotProps={{ htmlInput: { inputMode: 'email' } }}
              sx={{ flex: 1 }}
            />
          </Stack>
          <TextField
            label="Reply-to (optional)"
            value={replyTo}
            onChange={(e) => setReplyTo(e.target.value)}
            slotProps={{ htmlInput: { inputMode: 'email' } }}
          />
          <Stack>
            <FormControlLabel
              control={
                <Checkbox checked={bccSelf} onChange={(e) => setBccSelf(e.target.checked)} />
              }
              label="Send a copy to the from address"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={allowSelfSigned}
                  onChange={(e) => setAllowSelfSigned(e.target.checked)}
                />
              }
              label="Accept a self-signed certificate"
            />
          </Stack>
          <Box>
            <Typography sx={{ fontWeight: 600 }}>Documents this server emails</Typography>
            <Typography variant="body2" color="text.secondary">
              A document ticked here moves from its old server.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                mt: 1,
              }}
            >
              {EMAIL_MODELS.map((m) => {
                const now = owner(m.type);
                const elsewhere = now && now.serverId !== server?.id ? now.name : null;
                const checked = types.includes(m.type);
                return (
                  <FormControlLabel
                    key={m.type}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={(e) =>
                          setTypes((t) =>
                            e.target.checked ? [...t, m.type] : t.filter((x) => x !== m.type),
                          )
                        }
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{m.label}</Typography>
                        {elsewhere && (
                          <Typography
                            variant="caption"
                            color={checked ? 'warning.main' : 'text.secondary'}
                          >
                            {checked ? `Moves from ${elsewhere}` : `Sent by ${elsewhere}`}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                );
              })}
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" disabled={busy} onClick={submit}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TestDialog({ server, onClose }: { server: EmailServer; onClose: () => void }) {
  const { me } = useAuth();
  const admin = useEmailAdmin();
  const [to, setTo] = useState(me?.user.email ?? '');
  const result = admin.test.data;
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog open onClose={admin.test.isPending ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Test {server.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {result &&
            (result.ok ? (
              <Alert severity="success">Test email sent. Check the inbox of {to}.</Alert>
            ) : (
              <Alert severity="error">{result.error}</Alert>
            ))}
          <TextField
            label="Send the test to"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            slotProps={{ htmlInput: { inputMode: 'email' } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={admin.test.isPending}>
          Close
        </Button>
        <Button
          variant="contained"
          startIcon={<SendRounded />}
          disabled={admin.test.isPending || !to.trim()}
          onClick={() => {
            setError(null);
            admin.test.mutate(
              { id: server.id, to: to.trim() },
              { onError: (e) => setError(errorMessage(e)) },
            );
          }}
        >
          {admin.test.isPending ? 'Sending…' : 'Send test'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
